import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Lock, Zap } from "lucide-react";

const subcategories = {
  utenze: ["Luce", "Gas", "Fibra", "Controllo bollette", "Voltura", "Subentro", "Reclamo", "Rateizzazione", "Contratto non richiesto"],
  credito: ["Finanziamento", "Mutuo", "Cessione del quinto"],
  assicurazioni: ["Auto", "Moto", "Casa", "Vita"],
};

const typeLabels = { utenze: "Utenze", credito: "Credito", assicurazioni: "Assicurazioni" };

export default function Richieste() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const sub = user?.subscription || {};
  const [type, setType] = useState("utenze");
  const [form, setForm] = useState({ subcategory: "Luce", title: "", description: "" });
  const [loading, setLoading] = useState(false);

  const onType = (t) => {
    setType(t);
    setForm({ ...form, subcategory: subcategories[t][0] });
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/requests", { type, ...form });
      await refreshUser();
      toast.success("Richiesta inviata con successo");
      navigate("/area/pratiche");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore durante l'invio");
    } finally {
      setLoading(false);
    }
  };

  const creditoLocked = type === "credito" && !sub.active;

  const includedUsed = sub.requests_used || 0;
  const includedTotal = sub.requests_total || 3;
  const extraAvailable = Math.max(0, (sub.extra_total || 0) - (sub.extra_used || 0));
  const limitReached = sub.active && includedUsed >= includedTotal && extraAvailable === 0;

  const buyExtra = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/extra-checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Impossibile avviare il pagamento");
      setLoading(false);
    }
  };

  return (
    <div data-testid="richieste-page" className="mx-auto max-w-2xl space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold text-[#0A192F]">Nuova richiesta</h1>
        <p className="mt-1 text-slate-600">Scegli l'area e descrivi di cosa hai bisogno.</p>
      </div>

      {limitReached && (
        <div data-testid="richieste-limit-banner" className="rounded-2xl border border-[#F97316]/30 bg-[#F97316]/5 p-5">
          <p className="text-sm text-[#0A192F]">
            Hai utilizzato le 3 richieste incluse nel tuo Piano. Puoi continuare a utilizzare ViviTranquillo acquistando richieste extra a €19,90 ciascuna.
          </p>
          <button data-testid="richieste-buy-extra" type="button" onClick={buyExtra} disabled={loading} className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#F97316] px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#ea6a0c] disabled:opacity-60">
            <Zap className="h-4 w-4" /> {loading ? "..." : "Acquista Richiesta Extra · €19,90"}
          </button>
        </div>
      )}

      <form onSubmit={submit} className="space-y-6 rounded-2xl border border-slate-200 bg-white p-8">
        <div>
          <label className="mb-2 block text-sm font-semibold text-[#0A192F]">Area</label>
          <div className="grid grid-cols-3 gap-2">
            {Object.keys(subcategories).map((t) => (
              <button key={t} type="button" data-testid={`richiesta-type-${t}`} onClick={() => onType(t)} className={`rounded-xl border-2 py-3 text-sm font-semibold transition-colors ${type === t ? "border-[#10B981] bg-[#10B981]/5 text-[#0A192F]" : "border-slate-200 text-slate-500"}`}>
                {typeLabels[t]}
              </button>
            ))}
          </div>
        </div>

        {creditoLocked ? (
          <div data-testid="credito-locked" className="rounded-xl border border-[#F97316]/30 bg-[#F97316]/5 p-5 text-center">
            <Lock className="mx-auto h-6 w-6 text-[#F97316]" />
            <p className="mt-3 font-semibold text-[#0A192F]">Il supporto credito è riservato agli abbonati</p>
            <p className="mt-1 text-sm text-slate-600">Attiva il Piano €49/anno per aprire richieste di credito.</p>
            <button type="button" onClick={() => navigate("/area/piano")} className="mt-4 rounded-full bg-[#10B981] px-6 py-2.5 text-sm font-semibold text-white">Vai al Piano</button>
          </div>
        ) : (
          <>
            <div>
              <label className="mb-2 block text-sm font-semibold text-[#0A192F]">Categoria</label>
              <select data-testid="richiesta-subcategory" value={form.subcategory} onChange={(e) => setForm({ ...form, subcategory: e.target.value })} className="w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:ring-2 focus:ring-[#10B981]">
                {subcategories[type].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-2 block text-sm font-semibold text-[#0A192F]">Titolo</label>
              <input data-testid="richiesta-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Es. Voltura contratto luce" className="w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:ring-2 focus:ring-[#10B981]" />
            </div>
            <div>
              <label className="mb-2 block text-sm font-semibold text-[#0A192F]">Descrizione</label>
              <textarea data-testid="richiesta-description" rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Descrivi la tua richiesta..." className="w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:ring-2 focus:ring-[#10B981]" />
            </div>
            <button data-testid="richiesta-submit" disabled={loading} className="w-full rounded-full bg-[#10B981] px-8 py-3.5 font-semibold text-white transition-colors hover:bg-[#059669] disabled:opacity-60">
              {loading ? "Invio..." : "Invia richiesta"}
            </button>
          </>
        )}
      </form>
    </div>
  );
}
